import { crossCityIssues } from "./itinerary-cross-city.js";
import { detectLocalTimeDrift, ianaTimeZone, localDateOf } from "./time-zone.js";
export const itineraryIssueCodes = Object.freeze([
    "ITINERARY_EMPTY",
    "SECTION_POSITION_DUPLICATE",
    "ITEM_POSITION_DUPLICATE",
    "DAY_DATE_DUPLICATE",
    "DAY_MISSING_TIME_ZONE",
    "DAY_OUTSIDE_TRIP_DATES",
    "ITEM_INTERVAL_INVALID",
    "ITEM_MISSING_TIME_ZONE",
    "ITEM_TIME_ZONE_UNKNOWN",
    "ITEM_START_NOT_ON_DAY",
    "ITEM_SCHEDULED_IN_FLEXIBLE_SECTION",
    "ITEM_ALL_DAY_IN_FLEXIBLE_SECTION",
    "ITEMS_OVERLAP",
    "ITEM_LOCATION_MISSING",
    // Milestone D (contract `docs/architecture/milestone-d-contracts.md`, section 3.4). Names are frozen; the lane
    // named there emits each code. `validateItinerary` below emits `ITEM_LOCAL_TIME_DRIFT` and, from the deterministic
    // cross-city rule, `TRAVEL_TIME_EXCEEDS_GAP`; it emits no other code of this block.
    "TIME_NOT_EXISTENT",
    "TIME_AMBIGUOUS",
    "ITEM_LOCAL_TIME_DRIFT",
    "LOCATION_UNRESOLVED",
    "LOCATION_AMBIGUOUS",
    "ITEM_ZONE_LOCATION_MISMATCH",
    "TRAVEL_TIME_EXCEEDS_GAP",
    "TRAVEL_TIGHT",
    "ROUTE_UNVERIFIED",
    "WEATHER_RISK_HIGH",
    "WEATHER_RISK_SEVERE",
    "WEATHER_UNVERIFIED",
    "LINKED_ITEM_CHANGED",
]);
const MINUTE_MS = 60_000;
const byPositionThenId = (a, b) => a.position - b.position || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
const validInstant = (value) => value instanceof Date && !Number.isNaN(value.getTime());
function zoneOf(value) {
    if (value === null)
        return null;
    try {
        return ianaTimeZone(value);
    }
    catch {
        return null;
    }
}
/** `[start, end)` in milliseconds: the end, else the start plus the duration, else one millisecond. */
function interval(item) {
    if (item.timingKind !== "SCHEDULED" || !validInstant(item.startsAt))
        return null;
    const start = item.startsAt.getTime();
    let end;
    if (item.endsAt !== null) {
        if (!validInstant(item.endsAt))
            return null;
        end = item.endsAt.getTime();
        if (end < start)
            return null;
    }
    else if (item.durationMinutes !== null) {
        end = start + item.durationMinutes * MINUTE_MS;
    }
    else {
        end = start;
    }
    return [start, end > start ? end : start + 1];
}
const hasLocation = (item) => (item.locationName !== null && item.locationName.trim() !== "") ||
    (item.latitude !== null && item.longitude !== null);
/**
 * Structural and temporal validation of an itinerary, pure and total (it never throws on odd data).
 * Issues come in a stable order whatever the input order: the itinerary, then each section in
 * position order, then each item, then overlaps. A BLOCKING issue stops a DRAFT from becoming the
 * plan of record; a WARNING is shown and allowed. Physical feasibility (routes, opening hours,
 * weather) is not judged here: an activated itinerary stays `UNKNOWN` until Phase 9.
 *
 * An item must start on the local date of its day section, read in the item's own zone; it may
 * end on a later date (overnight transit). Two scheduled items of one section overlap when their
 * `[start, end)` intervals intersect: a blocking issue, except when one of them is a transit or a
 * buffer (a warning).
 */
export function validateItinerary(input) {
    const issues = [];
    const add = (code, severity, sectionId, itemId) => issues.push({ code, severity, sectionId, itemId });
    if (input.items.length === 0)
        add("ITINERARY_EMPTY", "BLOCKING", null, null);
    const sections = [...input.sections].sort(byPositionThenId);
    const sectionById = new Map(sections.map((section) => [section.id, section]));
    const positions = new Set();
    const dates = new Set();
    for (const section of sections) {
        if (positions.has(section.position)) {
            add("SECTION_POSITION_DUPLICATE", "BLOCKING", section.id, null);
        }
        positions.add(section.position);
        if (section.kind !== "DAY")
            continue;
        if (zoneOf(section.timeZone) === null)
            add("DAY_MISSING_TIME_ZONE", "BLOCKING", section.id, null);
        if (section.localDate !== null) {
            if (dates.has(section.localDate))
                add("DAY_DATE_DUPLICATE", "BLOCKING", section.id, null);
            dates.add(section.localDate);
            const trip = input.trip;
            if (trip !== null &&
                ((trip.startsOn !== null && section.localDate < trip.startsOn) ||
                    (trip.endsOn !== null && section.localDate > trip.endsOn))) {
                add("DAY_OUTSIDE_TRIP_DATES", "WARNING", section.id, null);
            }
        }
    }
    const items = [...input.items].sort((a, b) => (sectionById.get(a.sectionId)?.position ?? Number.MAX_SAFE_INTEGER) -
        (sectionById.get(b.sectionId)?.position ?? Number.MAX_SAFE_INTEGER) ||
        (a.sectionId < b.sectionId ? -1 : a.sectionId > b.sectionId ? 1 : 0) ||
        byPositionThenId(a, b));
    const itemPositions = new Set();
    for (const item of items) {
        const key = `${item.sectionId}:${item.position}`;
        if (itemPositions.has(key))
            add("ITEM_POSITION_DUPLICATE", "BLOCKING", null, item.id);
        itemPositions.add(key);
        const section = sectionById.get(item.sectionId);
        if (item.timingKind === "SCHEDULED") {
            if (interval(item) === null)
                add("ITEM_INTERVAL_INVALID", "BLOCKING", null, item.id);
            const zone = zoneOf(item.timeZone);
            if (item.timeZone === null)
                add("ITEM_MISSING_TIME_ZONE", "BLOCKING", null, item.id);
            else if (zone === null)
                add("ITEM_TIME_ZONE_UNKNOWN", "BLOCKING", null, item.id);
            if (section?.kind === "FLEXIBLE") {
                add("ITEM_SCHEDULED_IN_FLEXIBLE_SECTION", "BLOCKING", section.id, item.id);
            }
            else if (section !== undefined &&
                zone !== null &&
                validInstant(item.startsAt) &&
                localDateOf(item.startsAt, zone) !== section.localDate) {
                add("ITEM_START_NOT_ON_DAY", "BLOCKING", section.id, item.id);
            }
            const drift = detectLocalTimeDrift({
                start: {
                    enteredLocal: item.enteredLocalStart ?? null,
                    enteredUtcOffsetMinutes: item.enteredUtcOffsetMinutes ?? null,
                    timeZone: item.timeZone,
                    instant: item.startsAt,
                },
                end: {
                    enteredLocal: item.enteredLocalEnd ?? null,
                    enteredUtcOffsetMinutes: item.enteredEndUtcOffsetMinutes ?? null,
                    timeZone: item.endTimeZone ?? item.timeZone,
                    instant: item.endsAt,
                },
            });
            if (drift.drifted)
                add("ITEM_LOCAL_TIME_DRIFT", "WARNING", null, item.id);
        }
        else if (item.timingKind === "ALL_DAY" && section?.kind === "FLEXIBLE") {
            add("ITEM_ALL_DAY_IN_FLEXIBLE_SECTION", "BLOCKING", section.id, item.id);
        }
        if (item.kind === "CUSTOM_ACTIVITY" && item.status !== "CANCELLED" && !hasLocation(item)) {
            add("ITEM_LOCATION_MISSING", "WARNING", null, item.id);
        }
    }
    const bySection = new Map();
    for (const item of items) {
        if (item.status === "CANCELLED" || interval(item) === null)
            continue;
        const list = bySection.get(item.sectionId) ?? [];
        list.push(item);
        bySection.set(item.sectionId, list);
    }
    for (const list of bySection.values()) {
        for (let later = 1; later < list.length; later += 1) {
            const current = list[later];
            const [start, end] = interval(current);
            for (let earlier = 0; earlier < later; earlier += 1) {
                const other = list[earlier];
                const [otherStart, otherEnd] = interval(other);
                if (start < otherEnd && otherStart < end) {
                    const soft = current.kind === "TRANSIT" ||
                        current.kind === "BUFFER" ||
                        other.kind === "TRANSIT" ||
                        other.kind === "BUFFER";
                    add("ITEMS_OVERLAP", soft ? "WARNING" : "BLOCKING", current.sectionId, current.id);
                    break;
                }
            }
        }
    }
    // The deterministic cross-city minimum (contract 5.1): it needs no provider, so it holds when a provider is down.
    issues.push(...crossCityIssues(sections, items));
    return Object.freeze(issues);
}
export const hasBlockingIssue = (issues) => issues.some((issue) => issue.severity === "BLOCKING");
